// A tiny path router: the vote page must not pay for a routing library.
import { parseCode } from "@flp/core";

export type Route =
  | { name: "create"; from?: string; template?: string; fromVote?: boolean; fromParty?: boolean; category?: string }
  | { name: "join" }
  | { name: "vote"; code: string }
  | { name: "present"; code: string }
  | { name: "remote"; code: string }
  | { name: "my" }
  | { name: "admin" }
  | { name: "signin" }
  | { name: "upgrade" }
  | { name: "help" }
  | { name: "checkout" }
  | { name: "checkout-fake" }
  | { name: "checkout-success" }
  | { name: "signin-confirm" }
  | { name: "notfound" };

export function match(pathname: string, search = ""): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/" || path === "/new") {
    const q = new URLSearchParams(search);
    const from = q.get("from");
    const template = q.get("template");
    return {
      name: "create",
      from: from && /^\d{6}$/.test(from) ? from : undefined,
      // A template id from the site's library ("classroom/exit-ticket"); unknown ids open an empty creator.
      template: template && /^[a-z]+\/[a-z0-9-]{1,60}$/.test(template) ? template : undefined,
      // Opened from a vote page's link (growth plan item 11), counted on the server.
      fromVote: q.get("via") === "vote" || undefined,
      // Opened from a Poll Party page (2026-11-04), counted on the server when the poll is published.
      fromParty: q.get("via") === "party" || undefined,
      // A template category to offer as chips over an EMPTY creator (the vote page's host card: ?cat=contests).
      category: /^[a-z-]{1,40}$/.test(q.get("cat") ?? "") ? q.get("cat")! : undefined,
    };
  }
  if (path === "/join") return { name: "join" };
  if (path === "/my") return { name: "my" };
  if (path === "/admin") return { name: "admin" };
  if (path === "/signin") return { name: "signin" };
  if (path === "/upgrade") return { name: "upgrade" };
  if (path === "/help") return { name: "help" };
  if (path === "/checkout") return { name: "checkout" };
  if (path === "/checkout/fake") return { name: "checkout-fake" };
  if (path === "/checkout/success") return { name: "checkout-success" };
  if (path === "/signin/confirm") return { name: "signin-confirm" };
  const m = /^\/([^/]+)(?:\/(live|remote))?$/.exec(path);
  if (m) {
    const code = parseCode(m[1]!);
    if (code) return m[2] === "live" ? { name: "present", code } : m[2] === "remote" ? { name: "remote", code } : { name: "vote", code };
  }
  return { name: "notfound" };
}

const listeners = new Set<() => void>();
export function onRouteChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
addEventListener("popstate", () => listeners.forEach((f) => f()));

export function navigate(to: string): void {
  history.pushState(null, "", to);
  scrollTo(0, 0);
  listeners.forEach((f) => f());
}
